import { JobSummary, JobSummaryId } from 'api/hooks/useGetJobSummaries'

import { JobType } from 'job-lib/types/JobType'

export const initialPipelineTreeReducerState: PipelineTreeState = {
  pipelineTree: [],
  folderPath: null,
  deletedPipelines: [],
  isCreatingFolder: false,
  isDeletingFolder: false,
  hasReachedFolderLimit: false
}

export type PipelineTree = PipelineNode[]

export type PipelineNodeType = 'pipeline' | 'folder'

export interface PipelineNode {
  /**
   * The name of the pipeline or folder.
   * For a pipeline this is the name without the path or extension.
   * For a folder this is just the folder name with no ancestors.
   */
  name: string

  /**
   * Whether the node is in the root (top-level).
   */
  root: boolean

  /**
   * The fully qualified path to the pipeline or folder.
   * For a pipeline this the ID (path + name + extension).
   * For a folder this is the full path from the root.
   */
  path: string

  /**
   * The pipeline summary object. Only assigned for
   * nodes that are pipelines.
   */
  pipeline?: JobSummary

  /**
   * The type of node. Folder or pipeline.
   */
  type: PipelineNodeType

  /**
   * Child nodes that sit inside the current one.
   * Only applicable for folder nodes.
   */
  children: PipelineNode[]
}

export enum PipelineTreeReducerType {
  /**
   * Adds a new folder to the pipeline tree at the given path.
   */
  ADD_FOLDER,

  /**
   * Adds a new folder to the root of the pipeline tree.
   */
  ADD_ROOT_FOLDER,

  /**
   * Removes a single folder from the tree.
   */
  REMOVE_FOLDER,

  /**
   * Removes a single pipeline from the tree.
   */
  REMOVE_PIPELINE,

  /**
   * Moves a pipeline from one folder to another.
   */
  MOVE_PIPELINE,

  /**
   * Overwrites all pipeline tree data with a new structure
   * built from an array of pipeline summaries. Any empty folders
   * currently persisted client-side will be first pruned based on
   * the folder nodes present in the summaries, and then the resulting
   * folders will be merged into the tree.
   */
  OVERWRITE_TREE,

  /**
   * Initiates the folder creation flow. This action is not
   * responsible for actually creating a folder, but to render
   * the modal instead.
   */
  CREATE_FOLDER,

  /**
   * Handles the deletion of a single folder node and all its children from
   * the tree.
   *
   * Sets a  collection of pipeline IDs in the state for all
   * the pipelines that were nested under the deleted folder. The tree itself
   * is not actually mutated (unless the folder is empty) as it is rebuilt
   * from the server-side WTS data after the deletion has happened.
   */
  DELETE_FOLDER,

  /**
   * Manually sets the folder path in the pipeline tree state. Used
   * to ensure that a pipeline created in a folder is prepended with
   * the path of that folder upon creation.
   */
  SET_FOLDER_PATH,

  /**
   * Resets the pipeline tree to its default state, excluding
   * the tree data itself.
   */
  RESET
}

export interface PipelineTreeAction {
  /**
   * The type of command to be dispatched.
   */
  type: PipelineTreeReducerType

  /**
   * The raw name of the pipeline entered by the user
   * to act upon. Does not include the folder path or
   * the file extension.
   */
  name?: string

  /**
   * The current branch checkout out in Git.
   */
  branch?: string

  /**
   * The current project name that is active.
   */
  project?: string

  /**
   * The folder path the pipeline from the root of the project.
   * Should not contain a trailing slash.
   */
  path?: string

  /**
   * The type of the pipeline that is being acted upon.
   */
  pipelineType?: JobType

  /**
   * The target directory for the given action. This should
   * be a full file path from the root of the project, with
   * no trailing slash.
   */
  targetDirectory?: string

  /**
   * An array of pipeline summaries for the most up-to-date
   * working tree copy for the project.
   */
  summaries?: JobSummary[]
}

export interface PipelineTreeState {
  /**
   * The path of the folder in question. Used by several folder actions.
   * A null folder path indicates the root directory.
   */
  folderPath: string | null

  /**
   * The pipeline tree itself. This is the main piece of state that tracks
   * the whole tree data structure of pipeline and folder nodes.
   */
  pipelineTree: PipelineTree

  /**
   * Indicates that the user has actioned the creation of a folder.
   * Drives conditional rendering logic such as the create folder modal.
   */
  isCreatingFolder: boolean

  /**
   * Indicates that the user has actioned the deletion of a folder.
   * Drives conditional rendering logic such as the delete folder modal.
   */
  isDeletingFolder: boolean

  /**
   * Indicates that the user has reached the maximum number of nested folders
   * for the current (create folder) operation. Drives logic to ensure the
   * user cannot create more folders that the limit.
   */
  hasReachedFolderLimit: boolean

  /**
   * An array of pipeline IDs that are to be deleted.
   */
  deletedPipelines: JobSummaryId[]
}
